import React, { useState } from 'react';

/**
 * Vibrant branded loading screen for app bootstrap and data fetches.
 */
const LoadingScreen = ({
  message = 'Loading data...',
  subMessage = 'Please wait while we fetch your information',
  fullScreen = true,
  size = 'large',
  showFallbackLink = false,
}) => {
  const [logoFailed, setLogoFailed] = useState(false);

  const ringSizes = {
    small: 'h-14 w-14',
    medium: 'h-20 w-20',
    large: 'h-28 w-28',
  };

  const logoSizes = {
    small: 'h-8 w-8',
    medium: 'h-11 w-11',
    large: 'h-14 w-14',
  };

  const containerClasses = fullScreen
    ? 'fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden acctoo-loading-screen'
    : 'w-full flex items-center justify-center py-12 acctoo-loading-screen acctoo-loading-screen--inline';

  return (
    <div className={containerClasses} role="status" aria-live="polite" aria-busy="true">
      {/* Animated gradient backdrop */}
      <div className="acctoo-loading-bg" aria-hidden />
      <div className="acctoo-loading-orb acctoo-loading-orb--1" aria-hidden />
      <div className="acctoo-loading-orb acctoo-loading-orb--2" aria-hidden />
      <div className="acctoo-loading-orb acctoo-loading-orb--3" aria-hidden />
      <div className="acctoo-loading-grid" aria-hidden />

      <div className="relative z-10 w-full max-w-sm px-5 sm:px-6">
        <div className="acctoo-loading-card text-center">
          {/* Logo + spinner rings */}
          <div className="relative mx-auto mb-6 flex h-32 w-32 items-center justify-center">
            <div className={`${ringSizes[size]} acctoo-loading-ring acctoo-loading-ring--outer absolute`} />
            <div className={`${ringSizes[size]} acctoo-loading-ring acctoo-loading-ring--inner absolute`} />
            <div className="relative z-10 flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-white shadow-lg shadow-sky-500/25 ring-1 ring-white/80">
              {!logoFailed ? (
                <img
                  src={`${process.env.PUBLIC_URL || ''}/acctoo-logo.png`}
                  alt=""
                  className={`${logoSizes[size]} object-contain drop-shadow-sm`}
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <span className="font-acctoo-display text-2xl font-extrabold bg-gradient-to-br from-sky-500 via-cyan-500 to-indigo-600 bg-clip-text text-transparent">
                  A
                </span>
              )}
            </div>
          </div>

          <p className="font-acctoo-display text-xs font-bold uppercase tracking-[0.35em] text-sky-600/90 mb-2">
            AccToo
          </p>
          <h3 className="font-acctoo-display text-xl sm:text-2xl font-bold text-slate-800 mb-1.5">
            {message}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">
            {subMessage}
          </p>

          {/* Progress bar */}
          <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-slate-200/80">
            <div className="acctoo-loading-progress h-full rounded-full" />
          </div>

          {/* Bouncing dots */}
          <div className="mt-5 flex justify-center gap-2">
            <span className="acctoo-loading-dot bg-sky-500" style={{ animationDelay: '0ms' }} />
            <span className="acctoo-loading-dot bg-cyan-400" style={{ animationDelay: '120ms' }} />
            <span className="acctoo-loading-dot bg-indigo-500" style={{ animationDelay: '240ms' }} />
            <span className="acctoo-loading-dot bg-violet-500" style={{ animationDelay: '360ms' }} />
          </div>

          {showFallbackLink && (
            <div className="mt-6 pt-4 border-t border-slate-200/80">
              <p className="text-xs text-slate-500 mb-2">Taking longer than usual?</p>
              <a
                href="/login"
                className="text-sm font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-2"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/login';
                }}
              >
                Go to Login Page
              </a>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .acctoo-loading-screen {
          --acctoo-sky: #0ea5e9;
          --acctoo-cyan: #22d3ee;
          --acctoo-indigo: #6366f1;
          --acctoo-violet: #8b5cf6;
        }

        .acctoo-loading-screen--inline {
          min-height: 16rem;
          border-radius: 1rem;
          position: relative;
          overflow: hidden;
        }

        .acctoo-loading-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            #e0f2fe 0%,
            #bae6fd 18%,
            #a5f3fc 35%,
            #c7d2fe 55%,
            #ddd6fe 72%,
            #e0f2fe 100%
          );
          background-size: 200% 200%;
          animation: acctoo-loading-gradient 8s ease infinite;
        }

        .acctoo-loading-orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(48px);
          opacity: 0.55;
          pointer-events: none;
        }

        .acctoo-loading-orb--1 {
          width: 18rem;
          height: 18rem;
          top: -4rem;
          left: -3rem;
          background: radial-gradient(circle, #38bdf8 0%, transparent 70%);
          animation: acctoo-loading-float 7s ease-in-out infinite;
        }

        .acctoo-loading-orb--2 {
          width: 22rem;
          height: 22rem;
          bottom: -6rem;
          right: -4rem;
          background: radial-gradient(circle, #818cf8 0%, transparent 70%);
          animation: acctoo-loading-float 9s ease-in-out infinite reverse;
        }

        .acctoo-loading-orb--3 {
          width: 14rem;
          height: 14rem;
          top: 40%;
          right: 10%;
          background: radial-gradient(circle, #22d3ee 0%, transparent 70%);
          animation: acctoo-loading-float 6s ease-in-out infinite 1s;
        }

        .acctoo-loading-grid {
          position: absolute;
          inset: 0;
          opacity: 0.12;
          background-image:
            linear-gradient(rgba(14, 165, 233, 0.35) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.35) 1px, transparent 1px);
          background-size: 32px 32px;
          mask-image: radial-gradient(ellipse 80% 70% at 50% 45%, black 20%, transparent 75%);
        }

        .acctoo-loading-card {
          border-radius: 1.5rem;
          padding: 1.75rem 1.5rem 1.5rem;
          background: rgba(255, 255, 255, 0.82);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.9);
          box-shadow:
            0 25px 50px -12px rgba(14, 165, 233, 0.18),
            0 0 0 1px rgba(14, 165, 233, 0.06);
        }

        .acctoo-loading-ring {
          border-radius: 9999px;
          border: 3px solid transparent;
        }

        .acctoo-loading-ring--outer {
          border-top-color: var(--acctoo-sky);
          border-right-color: var(--acctoo-cyan);
          animation: acctoo-loading-spin 1.4s linear infinite;
        }

        .acctoo-loading-ring--inner {
          border-bottom-color: var(--acctoo-indigo);
          border-left-color: var(--acctoo-violet);
          animation: acctoo-loading-spin 1s linear infinite reverse;
          transform: scale(0.82);
        }

        .acctoo-loading-progress {
          width: 40%;
          background: linear-gradient(90deg, var(--acctoo-sky), var(--acctoo-cyan), var(--acctoo-indigo), var(--acctoo-violet));
          background-size: 200% 100%;
          animation: acctoo-loading-sweep 1.8s ease-in-out infinite;
        }

        .acctoo-loading-dot {
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 9999px;
          animation: acctoo-loading-bounce 0.9s ease-in-out infinite;
        }

        @keyframes acctoo-loading-gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes acctoo-loading-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(12px, -16px) scale(1.05); }
        }

        @keyframes acctoo-loading-spin {
          to { transform: rotate(360deg); }
        }

        .acctoo-loading-ring--inner {
          animation: acctoo-loading-spin-reverse 1s linear infinite;
        }

        @keyframes acctoo-loading-spin-reverse {
          to { transform: scale(0.82) rotate(-360deg); }
        }

        @keyframes acctoo-loading-sweep {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(320%); }
        }

        @keyframes acctoo-loading-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40% { transform: translateY(-6px); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .acctoo-loading-bg,
          .acctoo-loading-orb,
          .acctoo-loading-ring,
          .acctoo-loading-progress,
          .acctoo-loading-dot {
            animation: none !important;
          }
          .acctoo-loading-progress {
            width: 60%;
            margin: 0 auto;
          }
        }
      `}</style>
    </div>
  );
};

/** Compact inline spinner — brand colors */
export const LoadingSpinner = ({ size = 'medium', className = '' }) => {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-6 w-6',
    large: 'h-8 w-8',
  };

  return (
    <div className={`flex items-center justify-center ${className}`} role="status" aria-label="Loading">
      <div
        className={`${sizeClasses[size]} rounded-full border-2 border-sky-200 border-t-sky-500 border-r-cyan-400 animate-spin`}
      />
    </div>
  );
};

/** Skeleton placeholder rows */
export const SkeletonLoader = ({ count = 3, className = '' }) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded w-3/4 mb-2" />
          <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
};

export default LoadingScreen;
